import type { BondOrder } from './editor';
import type { DoubleBondStereo, DrawingDocument, Point2D, StereoLigand, TetrahedralStereo } from '../model';
import type { PaperSize } from './geometry';
import { validateDrawingDocument } from './drawingValidation.ts';
import { CALCULATED_PROVENANCE } from './documentSelectors.ts';
import { createId, createSequentialIds, roundCoordinate } from './drawingDocumentCore.ts';
import { regionalDefaultPage } from './page.ts';

export interface ChemicalStructureAtom {
  readonly atomicNumber: number;
  readonly formalCharge: number;
  readonly x: number;
  readonly y: number;
  readonly aromatic?: boolean;
  readonly stereoParity?: 'clockwise' | 'counterclockwise';
  readonly stereoDescriptor?: 'R' | 'S';
  readonly isotope?: number;
  readonly radicalElectronCount?: number;
}

export interface ChemicalStructureBond {
  readonly sourceAtomIndex: number;
  readonly targetAtomIndex: number;
  readonly order: BondOrder;
  readonly aromatic?: boolean;
  readonly wedgeStyle?: 'solid' | 'hashed';
  readonly wedgeFromAtomIndex?: number;
  /** A double bond whose cis/trans is left open (B3 #271), drawn crossed (#1059). */
  readonly crossed?: boolean;
  readonly stereoConfiguration?: 'cis' | 'trans';
  readonly stereoAtomIndexes?: readonly [number, number];
}

export function createEmptyDrawingDocument(size: PaperSize): DrawingDocument {
  const graphId = createId('graph');
  return {
    schemaVersion: 'chem-drawing.v10',
    structure: {
      id: graphId,
      revision: 1,
      graph: { id: graphId, revision: 1, nodes: [], edges: [] },
      atoms: {},
      bonds: {},
      formalCharges: {},
      stereochemistry: { tetrahedral: {}, doubleBonds: {} }
    },
    depiction: {
      id: createId('depiction'),
      revision: 1,
      structureId: graphId,
      structureRevision: 1,
      coordinateSet: {
        id: createId('coords'),
        kind: 'depiction-2d',
        structureId: graphId,
        structureRevision: 1,
        dimensions: 2,
        units: 'drawing-unit',
        width: Math.max(1, size.width),
        height: Math.max(1, size.height),
        coordinates: {}
      },
      atomLabels: {},
      bondStyles: {},
      bondLengthOverrides: {}
    },
    annotations: [],
    derivedPropertySets: [],
    // #772 -- a new drawing keeps the page it was created with (Letter or A4 by region).
    page: regionalDefaultPage()
  };
}

interface FittedLayout {
  readonly coordinates: readonly Point2D[];
  // The coordinate space the atoms were laid out in - equal to the passed-in `size` for a
  // molecule that fits comfortably, or larger than it for one that doesn't (see below).
  readonly size: PaperSize;
}

function fittedCoordinates(atoms: readonly ChemicalStructureAtom[], size: PaperSize): FittedLayout {
  if (atoms.length === 0) return { coordinates: [], size };
  const width = Math.max(1, size.width);
  const height = Math.max(1, size.height);
  const xs = atoms.map((atom) => atom.x);
  const ys = atoms.map((atom) => atom.y);
  const minimumX = Math.min(...xs);
  const maximumX = Math.max(...xs);
  const minimumY = Math.min(...ys);
  const maximumY = Math.max(...ys);
  const sourceWidth = maximumX - minimumX;
  const sourceHeight = maximumY - minimumY;
  const padding = Math.min(48, width * 0.1, height * 0.1);
  // Always lay out at the reference bond-length scale (30 reproduces DEFAULT_BOND_DRAWING_CONFIG's
  // 45px against RDKit's ~1.5-unit bond spacing) instead of shrinking it to force-fit a fixed
  // canvas - a molecule that needs more room than `size` gets a larger coordinate space instead
  // of arbitrarily shrunk, eventually-illegible bonds. `size` is a floor, not a cap, so a small
  // molecule still gets a comfortable, centered canvas rather than one sized to its own tiny extent.
  const scale = 30;
  const contentWidth = Math.max(width, sourceWidth * scale + 2 * padding);
  const contentHeight = Math.max(height, sourceHeight * scale + 2 * padding);
  const sourceCenterX = (minimumX + maximumX) / 2;
  const sourceCenterY = (minimumY + maximumY) / 2;
  const coordinates = atoms.map((atom) => ({
    x: roundCoordinate(contentWidth / 2 + (atom.x - sourceCenterX) * scale),
    y: roundCoordinate(contentHeight / 2 - (atom.y - sourceCenterY) * scale)
  }));
  return { coordinates, size: { width: contentWidth, height: contentHeight } };
}

export function createDrawingDocumentFromChemicalStructure(
  size: PaperSize,
  atoms: readonly ChemicalStructureAtom[],
  bonds: readonly ChemicalStructureBond[]
): DrawingDocument {
  if (atoms.length === 0) throw new Error('A chemical structure must contain at least one atom.');
  for (const atom of atoms) {
    if (
      !Number.isInteger(atom.atomicNumber) ||
      atom.atomicNumber < 1 ||
      atom.atomicNumber > 118 ||
      !Number.isInteger(atom.formalCharge) ||
      !Number.isFinite(atom.x) ||
      !Number.isFinite(atom.y)
    ) {
      throw new Error('The imported chemical structure contains invalid atom data.');
    }
  }
  const graphId = createId('graph');
  const nodes = createSequentialIds(atoms.length, 'n').map((id) => ({ id }));
  const fitted = fittedCoordinates(atoms, size);
  const edgeIds = createSequentialIds(bonds.length, 'e');
  const edges = bonds.map((bond, index) => {
    const source = nodes[bond.sourceAtomIndex];
    const target = nodes[bond.targetAtomIndex];
    if (!source || !target || source.id === target.id || ![1, 2, 3, 4, 5, 6].includes(bond.order)) {
      throw new Error('The imported chemical structure contains invalid bond data.');
    }
    return { id: edgeIds[index]!, source: source.id, target: target.id };
  });
  const graph = { id: graphId, revision: 1, nodes, edges };
  const tetrahedral: Record<string, TetrahedralStereo> = Object.fromEntries(
    nodes.flatMap((node, index) => {
      const atom = atoms[index]!;
      if (!atom.stereoParity) return [];
      const neighbors = edges.flatMap((edge) =>
        edge.source === node.id ? [edge.target] : edge.target === node.id ? [edge.source] : []
      );
      if (neighbors.length !== 3 && neighbors.length !== 4) return [];
      const ligands: StereoLigand[] = neighbors.map((atomId) => ({ atomId }));
      if (ligands.length === 3) ligands.push({ implicitHydrogen: true });
      return [
        [
          node.id,
          {
            ligands: ligands as unknown as readonly [StereoLigand, StereoLigand, StereoLigand, StereoLigand],
            winding: atom.stereoParity
          }
        ]
      ];
    })
  );
  const doubleBonds: Record<string, DoubleBondStereo> = Object.fromEntries(
    edges.flatMap((edge, index) => {
      const bond = bonds[index]!;
      return bond.stereoConfiguration && bond.stereoAtomIndexes
        ? [
            [
              edge.id,
              {
                relativeConfiguration: bond.stereoConfiguration,
                referenceAtomIds: bond.stereoAtomIndexes.map((atomIndex) => nodes[atomIndex]!.id) as [string, string]
              }
            ]
          ]
        : [];
    })
  );
  const aromaticAtomIds = nodes.flatMap((node, index) => (atoms[index]!.aromatic ? [node.id] : []));
  const aromaticBondIds = edges.flatMap((edge, index) => (bonds[index]!.aromatic ? [edge.id] : []));
  const atomDescriptors = Object.fromEntries(
    nodes.flatMap((node, index) => (atoms[index]!.stereoDescriptor ? [[node.id, atoms[index]!.stereoDescriptor!]] : []))
  );
  const document: DrawingDocument = {
    schemaVersion: 'chem-drawing.v10',
    structure: {
      id: graphId,
      revision: 1,
      graph,
      atoms: Object.fromEntries(
        nodes.map((node, index) => {
          const atom = atoms[index]!;
          return [
            node.id,
            {
              atomicNumber: atom.atomicNumber,
              ...(atom.isotope ? { isotope: atom.isotope } : {}),
              ...(atom.radicalElectronCount ? { radicalElectronCount: atom.radicalElectronCount } : {})
            }
          ];
        })
      ),
      bonds: Object.fromEntries(
        edges.map((edge, index) => [edge.id, { order: bonds[index]!.order, type: 'covalent' as const }])
      ),
      formalCharges: Object.fromEntries(nodes.map((node, index) => [node.id, atoms[index]!.formalCharge])),
      stereochemistry: { tetrahedral, doubleBonds }
    },
    depiction: {
      id: createId('depiction'),
      revision: 1,
      structureId: graphId,
      structureRevision: 1,
      coordinateSet: {
        id: createId('coords'),
        kind: 'depiction-2d',
        structureId: graphId,
        structureRevision: 1,
        dimensions: 2,
        units: 'drawing-unit',
        width: fitted.size.width,
        height: fitted.size.height,
        coordinates: Object.fromEntries(nodes.map((node, index) => [node.id, fitted.coordinates[index]!]))
      },
      atomLabels: {},
      bondStyles: Object.fromEntries(
        edges.flatMap((edge, index): [string, DrawingDocument['depiction']['bondStyles'][string]][] => {
          const bond = bonds[index]!;
          if (bond.wedgeStyle && bond.wedgeFromAtomIndex !== undefined) {
            return [
              [
                edge.id,
                {
                  style: bond.wedgeStyle === 'solid' ? ('solid-wedge' as const) : ('hashed-wedge' as const),
                  fromAtomId: nodes[bond.wedgeFromAtomIndex]!.id
                }
              ]
            ];
          }
          if (bond.crossed && bond.order === 2) return [[edge.id, { style: 'crossed-double' }]];
          return [];
        })
      ),
      bondLengthOverrides: {}
    },
    annotations: [],
    derivedPropertySets: [
      ...(aromaticAtomIds.length || aromaticBondIds.length
        ? [
            {
              id: createId('aromaticity'),
              kind: 'aromaticity' as const,
              structureId: graphId,
              structureRevision: 1,
              atomIds: aromaticAtomIds,
              bondIds: aromaticBondIds,
              provenance: CALCULATED_PROVENANCE
            }
          ]
        : []),
      ...(Object.keys(atomDescriptors).length
        ? [
            {
              id: createId('cip'),
              kind: 'cip' as const,
              structureId: graphId,
              structureRevision: 1,
              atomDescriptors,
              bondDescriptors: {},
              provenance: CALCULATED_PROVENANCE
            }
          ]
        : [])
    ]
  };
  return validateDrawingDocument(document);
}
